import { SxProp, View } from 'dripsy';
import React, { useContext } from 'react';

import { H6, TextBody, TextCaption } from '@/design-system/components/typography';
import { CurrenciesIcon } from '@/features/earn/components';
import { Tag } from '@/design-system/components/tag/Tag';
import { formatAmountByDecimals } from '@/utils/numberFormatting';
import { formatDate } from '@/utils/dateFormatting';
import { ActivityHistoryDetailsProps } from '../../screens/ActivityHistory/tabs/ActivityHistoryDetails.modal.web';
import { UniNativeModal } from '@/design-system/components/modal/nativeModal/UniNativeModal';
import { useModalView } from '@/design-system/components/modal';
import { CurrencyIconVariant } from '../CurrenciesIcon';
import { CurrenciesContext } from '@/currencies/currency.context';
import { t } from 'i18next';
import { getEarnCategoryTranslationKey } from '../../utils/getEarnCategoryTranslationKey';
import { getEarnTenorTranslationKey } from '../../utils/getEarnTenorTranslationKey';

export const ActivityHistoryDetailsNative: React.FC<ActivityHistoryDetailsProps> = ({ subscription }) => {
  const { getDecimalByAsset } = useContext(CurrenciesContext);
  const { closeModal } = useModalView({});

  const tenorTranslationKey = getEarnTenorTranslationKey(subscription.tenor);

  return (
    <UniNativeModal title={t('earn-invest::glossary.activity-details')} onBack={closeModal}>
      <View sx={styles.container}>
        <ModalBodyRow sx={{ mb: '$7' }}>
          <CurrenciesIcon
            asset={subscription.asset}
            subText={subscription.provider}
            variant={CurrencyIconVariant.large}
            textSize="textTitle"
            subTextSize="textCaption"
          />
          <Tag
            label={subscription.type}
            color={subscription.type === 'SUBSCRIPTION' ? '#E6F7FF' : '#E8F5E9'}
            textColor={subscription.type === 'SUBSCRIPTION' ? '#096DD9' : '#388E3C'}
          />
        </ModalBodyRow>
        <ModalBodyRow sx={{ mb: '$5' }}>
          <View sx={styles.amountBox}>
            <TextCaption secondary semiBold>
              {t('earn-invest::glossary.amount')}
            </TextCaption>
            <H6 bold sx={{ color: '$textThemeSecondary' }}>
              {formatAmountByDecimals({
                value: subscription.amount,
                decimals: getDecimalByAsset(subscription.asset),
                suffix: subscription.asset
              })}
            </H6>
          </View>
          <View sx={{ ...styles.amountBox, alignItems: 'flex-end' }}>
            <TextCaption secondary semiBold>
              {t('earn-invest::glossary.est-apy')}
            </TextCaption>
            <H6 bold>{formatAmountByDecimals({ value: subscription.estimatedApyPercent, decimals: 2, suffix: '%' })}</H6>
          </View>
        </ModalBodyRow>
        <ModalBodyDataRow sx={{ mb: '$3' }} label={t('earn-invest::glossary.date')} value={formatDate(subscription.subscriptionDate)} />
        <ModalBodyDataRow
          sx={{ mb: '$3' }}
          label={t('earn-invest::glossary.category')}
          value={t(getEarnCategoryTranslationKey(subscription.category))}
        />
        <ModalBodyDataRow
          sx={{ mb: '$3' }}
          label={t('earn-invest::glossary.tenor')}
          value={t(tenorTranslationKey.key, tenorTranslationKey.options)}
        />
        {subscription.maturityDate && (
          <ModalBodyDataRow sx={{ mb: '$3' }} label={t('earn-invest::glossary.maturity')} value={formatDate(subscription.maturityDate)} />
        )}
        <ModalBodyDataRow label="Ref ID" value={subscription.refId} />
      </View>
    </UniNativeModal>
  );
};

const ModalBodyRow: React.FC<{ sx?: SxProp }> = ({ sx, children }) => {
  return <View sx={{ ...styles.modalBodyRow, ...sx }}>{children}</View>;
};

const ModalBodyDataRow: React.FC<{ sx?: SxProp; label: string; value: string }> = ({ label, value, sx, children }) => {
  return (
    <ModalBodyRow sx={sx}>
      <TextBody secondary>{label}</TextBody>
      <TextBody>{value}</TextBody>
    </ModalBodyRow>
  );
};

enum StyleName {
  container = 'container',
  modalHeader = 'modalHeader',
  modalBody = 'modalBody',
  modalBodyRow = 'modalBodyRow',
  amountBox = 'amountBox'
}

const styles: Record<StyleName, SxProp> = {
  container: {
    paddingX: '$4',
    paddingY: '$5'
  },
  modalHeader: {
    flex: 1,
    padding: '$7',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  modalBody: {
    padding: '$7'
  },
  modalBodyRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start'
  },
  amountBox: {}
};
